/**
 * acciones — madre ÚNICA de ejecución de transiciones canónicas desde la PWA
 * (HTML-OPERABLE f6b8abd7, extraída de AccionesInstancia para no duplicar lógica:
 * los bloques IHC y el broker del visor de informes ejecutan por AQUÍ).
 *
 * Contrato inviolable: el front es PROYECCIÓN — la API canónica re-verifica
 * RBAC + FSM + DMN en cada ejecución. Nada en este módulo otorga capacidad.
 */
import { api, generateIdempotencyKey } from '@/lib/api'

/** Descompone el nombre canónico AARPIA.<sub>.<token>.<estado> en ruta. */
export function rutaCanonica(entidad: string): { sub: string; tk: string; estado: string } | null {
  const p = entidad.split('.')
  if (p.length < 4) return null
  return { sub: p[1], tk: p[2], estado: p[3] }
}

/**
 * Ejecuta una transición canónica con el JWT del usuario. `origen` discrimina la
 * Idempotency-Key por superficie (ihc / hop). v1.0 = semilla; el routing
 * version-agnóstico (lib/api CUI-2) la reescribe a la versión viva.
 */
export async function ejecutarVerboCanonico(
  entidad: string,
  instanciaId: string,
  verbo: string,
  token: string | null,
  origen: string,
): Promise<void> {
  const r = rutaCanonica(entidad)
  if (!r) throw new Error(`Entidad no canónica: ${entidad}`)
  await api.post(
    `/canonico/${r.sub}/${r.tk}/${r.estado}/v1.0/instancias/${instanciaId}/transiciones`,
    token,
    { verbo },
    generateIdempotencyKey(`${origen}-${verbo}`),
  )
}

// ── HOP-3: allowlist de FORMA del broker (f6b8abd7) ─────────────────────────
//
// El iframe del informe (origen opaco, sin token) solo puede PEDIR; el shell
// decide. Esta validación es PURA y estricta: todo mensaje que no cumpla la
// forma exacta se descarta (el caller emite WARN estructurado — nunca un error
// inducible desde el HTML del LLM).

export const TIPO_MENSAJE_ACCION = '__aarpia_accion__'
const RE_UUID_EXACTA = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
// Verbos FSM canónicos: MAYÚSCULAS (con _), 2..40 — p.ej. REVALIDAR, DESCARTAR.
const RE_VERBO = /^[A-Z][A-Z0-9_]{1,39}$/

export type MensajeAccion = { ok: true; instanciaId: string; verbo: string } | { ok: false; motivo: string }

/** Valida la FORMA de un postMessage de acción. No consulta nada: solo forma. */
export function validarMensajeAccion(data: unknown): MensajeAccion {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return { ok: false, motivo: 'no es objeto' }
  const d = data as Record<string, unknown>
  if (d.tipo !== TIPO_MENSAJE_ACCION) return { ok: false, motivo: 'tipo desconocido' }
  if (typeof d.instancia_id !== 'string' || !RE_UUID_EXACTA.test(d.instancia_id)) return { ok: false, motivo: 'instancia_id no es UUID' }
  if (typeof d.verbo !== 'string' || !RE_VERBO.test(d.verbo)) return { ok: false, motivo: 'verbo con forma inválida' }
  const claves = Object.keys(d)
  if (claves.length !== 3) return { ok: false, motivo: 'claves extra en el mensaje' }
  return { ok: true, instanciaId: d.instancia_id.toLowerCase(), verbo: d.verbo }
}
